import { useEffect, useRef } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useCurrencyStore } from '@/stores/currency-store';

// Channel name must match the one posted from routes/billing/checkout-success.tsx.
const CHECKOUT_CHANNEL = 'optidev-checkout';

export type CheckoutType = 'top-up' | 'cloud-add-funds';

/**
 * Listens for Stripe Checkout completion broadcast from the checkout-success page.
 * Fires `onSuccess` when a matching `type` message arrives (e.g. from the now-closed embed tab).
 */
export function useCheckoutSuccessListener(type: CheckoutType, onSuccess: () => void): void {
  const { getIdToken } = useAuth();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const getTokenRef = useRef(getIdToken);
  getTokenRef.current = getIdToken;

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(CHECKOUT_CHANNEL);
    channel.onmessage = (event: MessageEvent): void => {
      if (event.data?.type === type) {
        // A completed checkout (credit top-up or cloud add-funds, incl. the
        // embed new-tab flow) may have pinned the customer's Stripe currency —
        // refresh so the currency selector locks without a reload.
        void useCurrencyStore.getState().refresh(getTokenRef.current);
        onSuccessRef.current();
      }
    };

    return () => channel.close();
  }, [type]);
}
